import { FullScreenLoading } from "@senciamatch/frontend/components/FullScreenLoading";
import { fileParser } from "@senciamatch/frontend/libs/csv";
import { type BuildingStatus, buildingParser } from "@senciamatch/frontend/libs/csvParser/buildings";
import { trpc } from "@senciamatch/frontend/libs/trpc";
import type { Building } from "@senciamatch/shared/models/building";
import type { Company } from "@senciamatch/shared/models/company";
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { NewBuildingTable } from "./newBuildingTable";

export const NewBuildingPage: React.FC = () => {
  const [buildings, setBuildings] = useState<Building[]>([]);
  const [statuses, setStatuses] = useState<BuildingStatus>({});
  const [companies, setCompanies] = useState<Company[]>([]);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [progress, setProgress] = useState<{ current: number; total: number } | null>(null);
  const createBuildingsMutation = trpc.createBuildings.useMutation();
  const navigate = useNavigate();

  const utils = trpc.useContext();
  const getBuildings = trpc.getBuildings.useQuery();
  const existingBuildings = getBuildings.data?.buildings ?? [];

  const handleDrop = useCallback(
    async (e: React.DragEvent<HTMLDivElement>) => {
      e.preventDefault();
      const file = e.dataTransfer.files[0];
      if (!file) return;
      const data = await fileParser(file);
      if (!Array.isArray(data)) return;

      await utils.getBuildings.invalidate();

      const parsedData = await buildingParser.parse(data, existingBuildings);

      setBuildings(parsedData.buildings);
      setStatuses(parsedData.status);
      setCompanies(parsedData.companies);
    },
    [existingBuildings, utils.getBuildings.invalidate],
  );

  const handleDragOver = useCallback((e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
  }, []);

  const handleSubmit = useCallback(async () => {
    try {
      setIsSubmitting(true);
      setError(null);

      const params = {
        buildings: buildings.filter((building) => statuses[building.id] !== "persisted"),
        companies: companies,
      };

      if (params.buildings.length === 0 && params.companies.length === 0) {
        alert("登録するデータがありません");
        return;
      }

      const chunkSize = 100; // より小さなチャンクサイズに変更
      const totalBuildingChunks = Math.ceil(params.buildings.length / chunkSize);
      const totalCompanyChunks = Math.ceil(params.companies.length / chunkSize);
      const totalChunks = Math.max(totalBuildingChunks, totalCompanyChunks);

      setProgress({ current: 0, total: totalChunks });

      for (let i = 0; i < totalChunks; i++) {
        const buildingStart = i * chunkSize;
        const companyStart = i * chunkSize;

        const chunk = {
          buildings: params.buildings.slice(buildingStart, buildingStart + chunkSize),
          companies: params.companies.slice(companyStart, companyStart + chunkSize),
        };

        await createBuildingsMutation.mutateAsync(chunk);
        setProgress({ current: i + 1, total: totalChunks });
      }

      alert("登録が完了しました");
      navigate("/buildings");
    } catch (error) {
      console.error("登録エラー:", error);
      setError("登録中にエラーが発生しました。もう一度お試しください。");
    } finally {
      setIsSubmitting(false);
      setProgress(null);
    }
  }, [buildings, companies, createBuildingsMutation, statuses, navigate]);

  return (
    <div className="h-screen flex flex-col">
      <h2 className="text-2xl font-bold py-10 flex-shrink-0">物件の登録</h2>
      <div
        onDrop={handleDrop}
        onDragOver={handleDragOver}
        className="rounded-md border-dashed border-2 border-gray-200 bg-white p-4 min-h-40 flex flex-col justify-center items-center mb-10"
      >
        <p className="text-center">ファイルをここにドラッグ＆ドロップしてください</p>
      </div>

      {error && <p className="text-red-500 mb-4">{error}</p>}
      {buildings.length > 0 && (
        <div className="overflow-x-auto py-10 bg-white border border-base-100 px-8 flex-grow flex flex-col">
          <label className="flex items-center gap-2 mb-4">
            <input type="checkbox" className="checkbox" checked={showAll} onChange={() => setShowAll(!showAll)} />
            <span className="label-text">追加・更新分以外も表示</span>
          </label>
          <NewBuildingTable
            buildings={showAll ? buildings : buildings.filter((building) => statuses[building.id] !== "persisted")}
            companies={companies}
          />
          <button
            type="button"
            className={`px-4 py-2 rounded-md mt-4 flex items-center justify-center gap-2 ${
              isSubmitting ? "bg-gray-400 cursor-not-allowed" : "bg-primary-700 hover:bg-primary-800"
            } text-white`}
            onClick={handleSubmit}
            disabled={isSubmitting}
          >
            {isSubmitting && <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />}
            {isSubmitting ? "登録中..." : "新規登録"}
          </button>
        </div>
      )}

      {isSubmitting && (
        <>
          <FullScreenLoading />
          {progress && (
            <div className="fixed inset-0 flex items-center justify-center z-[1001]">
              <div className="bg-white p-6 rounded-lg shadow-lg">
                <p className="text-center mb-2">データを登録しています...</p>
                <p className="text-center text-sm text-gray-600">
                  {progress.current} / {progress.total} チャンク処理中
                </p>
                <div className="w-64 h-2 bg-gray-200 rounded-full mt-4">
                  <div
                    className="h-full bg-primary-700 rounded-full transition-all duration-300"
                    style={{ width: `${(progress.current / progress.total) * 100}%` }}
                  />
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
};
